import type {
  AccountSetup,
  AccountSetupInfoDto,
  LoginRequest,
  LoginResponse,
  MeResponse,
  MfaResendResponse,
  PasswordChange,
  SystemAdminLogin,
  SystemAdminLoginResponse,
} from "@eduleo/shared";
import { useQuery } from "@tanstack/react-query";
import { useSessionKey } from "../../app/session-sync";
import { apiRequest, refreshSession } from "../../lib/api-client";
import { useAuthStore } from "../../stores/auth-store";

export function login(credentials: LoginRequest): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/auth/login", {
    method: "POST",
    body: credentials,
    auth: false,
  });
}

/**
 * Eduleo's own team signs in with User ID and password only. With two-step verification on,
 * the answer is a challenge instead of a session: the code goes to `verifySystemAdminCode`.
 * Called exactly like the school sign-in (no token, same cookie handling).
 */
export function systemAdminLogin(credentials: SystemAdminLogin): Promise<SystemAdminLoginResponse> {
  return apiRequest<SystemAdminLoginResponse>("/auth/system-admin/login", {
    method: "POST",
    body: credentials,
    auth: false,
  });
}

/** The second step: the 6-digit code (or a recovery code) for a sign-in challenge. */
export function verifySystemAdminCode(input: {
  challenge: string;
  code: string;
}): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/auth/system-admin/mfa", {
    method: "POST",
    body: input,
    auth: false,
  });
}

/** Emails a fresh code for the same sign-in attempt. */
export function resendSystemAdminCode(challenge: string): Promise<MfaResendResponse> {
  return apiRequest<MfaResendResponse>("/auth/system-admin/mfa/resend", {
    method: "POST",
    body: { challenge },
    auth: false,
  });
}

/**
 * Choose your own password. Like refresh, it rotates the refresh cookie, so it carries the
 * client header; it answers with a fresh session and signs out every other device.
 */
/** Who an emailed "set your password" link is for. */
export function checkAccountSetup(token: string): Promise<AccountSetupInfoDto> {
  return apiRequest<AccountSetupInfoDto>("/auth/setup/check", {
    method: "POST",
    body: { token },
    auth: false,
  });
}

/** Chooses the password from the link and signs in (sets the refresh cookie, like sign-in). */
export function completeAccountSetup(input: AccountSetup): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/auth/setup", { method: "POST", body: input, auth: false });
}

export function changePassword(input: PasswordChange): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/auth/password", {
    method: "POST",
    body: input,
    clientHeader: true,
  });
}

export async function logout(): Promise<void> {
  try {
    await apiRequest<void>("/auth/logout", { method: "POST", auth: false, clientHeader: true });
  } finally {
    useAuthStore.getState().clearSession();
  }
}

/** On page load, turn the refresh cookie (if any) back into a session. */
export async function bootstrapSession(): Promise<void> {
  const restored = await refreshSession();
  if (!restored && useAuthStore.getState().status === "loading") {
    useAuthStore.getState().clearSession();
  }
}

export const meQueryKey = ["auth", "me"] as const;

/** The signed-in person and what they may do (used to show only usable menu items). */
export function useMe() {
  const session = useSessionKey();
  return useQuery({
    queryKey: [...meQueryKey, session],
    queryFn: ({ signal }) => apiRequest<MeResponse>("/auth/me", { signal }),
    enabled: Boolean(session),
    staleTime: 5 * 60_000,
  });
}
